import { useState, useMemo, useContext, useRef, useEffect } from 'react';
import { TextureLoader, DoubleSide } from 'three';

import back_fw from '@/assets/card/back/fw.webp';
import back_ss from '@/assets/card/back/ss.webp';
import back_summit from '@/assets/card/back/summit.webp';
import mat_blue from '@/assets/mats/blue.png';
import mat_green from '@/assets/mats/green.png';
import mat_pink from '@/assets/mats/pink.png';
import mat_yellow from '@/assets/mats/yellow.png';
import chime_low_sfx from '@/assets/sound_effects/chime_low_pass.mp3';
import my_turn_over_sfx from '@/assets/sound_effects/my_turn_over.mp3';
import your_turn_sfx from '@/assets/sound_effects/now_it_s_your_turn!.mp3';
import PhysicsFloor from '@/components/game/3d//physics/PhysicsFloor';
/* Card Builder */
import ActiveContractsAndAwardsBuilder from '@/components/game/3d/builder/card/ActiveContractsAndAwardsBuilder';
import CompletedContractsAndAwardsBuilder from '@/components/game/3d/builder/card/CompletedContractsAndAwardsBuilder';
import EquipmentBuilder from '@/components/game/3d/builder/card/EquipmentBuilder';
import FailedContractsBuilder from '@/components/game/3d/builder/card/FailedContractsBuilder';
import HandBuilder from '@/components/game/3d/builder/card/HandBuilder';
import EnergyBuilder from '@/components/game/3d/builder/energy/EnergyBuilder';
import EnergyBuilderCarbon from '@/components/game/3d/builder/energy/EnergyBuilderCarbon';
import EnergyBuilderDonation from '@/components/game/3d/builder/energy/EnergyBuilderDonation';
import PaymentAreaBounded from '@/components/game/3d/builder/PaymentAreaBounded';
/* Piece Builder */
import BatteryBuilder from '@/components/game/3d/builder/piece/BatteryBuilder';
import CarbonFootprintBuilder from '@/components/game/3d/builder/piece/CarbonFootprintBuilder';
import FactoryBuilder from '@/components/game/3d/builder/piece/FactoryBuilder';
import PrBuilder from '@/components/game/3d/builder/piece/PrBuilder';
import Button3D from '@/components/game/3d/object/button/Button3D';
import ButtonSquare3D from '@/components/game/3d/object/button/ButtonSquare3D';
import { DonationCup } from '@/components/game/3d/object/DonationCup';
import { createPayment } from '@/lyoc-common/src/interface/payment/factory';
import { GameContext, ViewContext } from '@/state/contexts';
import { performActionType } from '@/type/performAction';
import { logg } from '@/util/logg';
import { getPlayerStateWithId } from '@/util/player';
import { carbonStolenThisTurn } from '@lc/const/modifiers';
import {
	buyPR,
	donateEnergy,
	endTurn,
	stealCarbon,
	storeEnergy,
} from '@lc/const/socketEvent';
import { energyColors } from '@lc/types/Misc';

import { useTutorial } from '@/hooks/useTutorial';
import { tutStep_10, tutStep_11, tutStep_12, tutStep_13, tutStep_15, tutStep_18, tutStep_28, tutStep_30 } from '@/lyoc-common/src/const/tutorial';

const PlayermatBuilder = ({ playerId, rotato, matColor, matPathId }) => {
	const scale = 1.5;
	const playerMatWidth = 9 * scale;
	const playerMatHeight = 5 * scale;
	const showDonationCup = true;

	const yourTurnAudioRef: any = useRef(new Audio(your_turn_sfx));
	const chimeAudioRef: any = useRef(new Audio(chime_low_sfx));

	const { tutorialCheck } = useTutorial();

	function setRefParams(ref: any, volume: number = 0.25) {
		ref.current.loop = false;
		ref.current.volume = volume;
	}

	setRefParams(yourTurnAudioRef, 0.1);
	setRefParams(chimeAudioRef);

	const paymentBoxRef: any = useRef(null);

	const pc: any = useContext(GameContext);

	const {
		myState,
		activePlayerId,
		monthTracker,

		setPaymentTotal,
		paymentTotal,
		setPaymentObject,
		paymentObject,

		performAction,
		setSelectedAction,
		setSelectedActionCost,

		isTutorial,
		stepTutorial,
		setStepTutorial
	}: any = useContext(GameContext);

	const {
		isDebug,
		paymentMode,
		setPaymentMode, 
		currentTickerTimer,
		setCurrentTickerIndex
	}: any = useContext(ViewContext);

	const performActionTyped = performAction as performActionType;

	const {
		setPlayerMatPath,
		setShowPlayermatModal,

		pPE,
		pPC,
	}: any = useContext(ViewContext);

	const [fArr, setFArr] = useState([...myState.factoryEnergyArr]);
	const [bArr, setBArr] = useState([...myState.batteryEnergyArr]);
	const [numCarbon, setNumCarbon]: any = useState(myState.carbonReserve);

	const processedEnergyIds = useRef(new Set());

	useEffect(() => {
		processedEnergyIds.current.clear();

		const myFactoryEnergy = [...myState.factoryEnergyArr];
		const myBatteryEnergy = [...myState.batteryEnergyArr];

		setFArr(myFactoryEnergy);
		setBArr(myBatteryEnergy);
		setNumCarbon(myState.carbonReserve);

		logg('Payment tracking cache cleared for new transaction phase.');
	}, [paymentMode, myState.carbonReserve]);

	function playAudioOnce(audio: any) {
		if (audio.paused || audio.ended) {
			audio.currentTime = 0;
			audio.play().catch(console.error);
		} else {
			// Audio is already playing, do nothing
			console.log('Audio already playing, skipping.');
		}
	}

	useEffect(() => {
		if (activePlayerId === myState.playerId) {
			// playAudioOnce(yourTurnAudioRef.current);
			playAudioOnce(chimeAudioRef.current);
			return;
		}
		logg('no audio player', activePlayerId, myState.playerId);
	}, [activePlayerId, monthTracker]);

	function resetPaymentParams() {
		setPaymentMode(false);
		const emptyPayment = createPayment();
		setPaymentObject(emptyPayment);
		setPaymentTotal(0);
	}

	function updatePaymentTallyFactory(energyColor: energyColors) {
		logg('adding factory energy to payment object', energyColor);
		// remove 1 instance of selected color from fArr
		let tempArr = fArr;
		const index = tempArr.indexOf(energyColor);
		console.log(fArr);
		console.log(myState.factoryEnergyArr);
		console.log(index);
		if (index < 0) {
			console.log('sum ting wong');
			return;
		}
		tempArr.splice(index, 1);
		setFArr(tempArr);

		let newTally = paymentObject;
		newTally.factoryEnergyArr.push(energyColor);
		setPaymentObject(newTally);
		setPaymentTotal((paymentTotal) => paymentTotal + 1);
	}

	function updatePaymentTallyBattery(energyColor: energyColors) {
		// remove 1 instance of selected color from bArr
		let tempArr = bArr;
		const index = tempArr.indexOf(energyColor);
		if (index < 0) {
			return;
		}
		tempArr.splice(index, 1);
		setBArr(tempArr);

		let newTally = paymentObject;
		newTally.batteryEnergyArr.push(energyColor);
		setPaymentObject(newTally);
		setPaymentTotal(paymentTotal + 1);
	}

	function updatePaymentTallyCarbon() {
		if (numCarbon <= 0) {
			return;
		}

		let newTally = paymentObject;
		newTally.carbonReserve += 1;
		setPaymentObject(newTally);
		setPaymentTotal(paymentTotal + 1);
	}

	const path = [mat_yellow, mat_blue, mat_pink, mat_green];

	const playerMatPath = path[matPathId];

	function getMat() {
		return new TextureLoader().load(playerMatPath);
	}

	function getTextureSS() {
		return new TextureLoader().load(back_ss);
	}

	function getTextureFW() {
		return new TextureLoader().load(back_fw);
	}

	function getTextureSummit() {
		return new TextureLoader().load(back_summit);
	}

	const texture = useMemo(() => getMat(), []);
	const textureSS = useMemo(() => getTextureSS(), []);
	const textureFW = useMemo(() => getTextureFW(), []);
	const textureSummit = useMemo(() => getTextureSummit(), []);

	const playerState = getPlayerStateWithId(pc.playerStateArray, playerId);

	function viewPlayermatModal() {
		if (isDebug) {
			setPlayerMatPath(playerMatPath);
			setShowPlayermatModal(true);
		}
	}

	const handleEnergyPaymentZoneEntry = (energyData) => {
		const { id, energyColor, type } = energyData;
		logg('handling zone entry');

		// Check if it belongs to the current player action
		if (playerId !== activePlayerId) return;

		// Fast return if this energy ID was already spent
		if (processedEnergyIds.current.has(id)) return;
		processedEnergyIds.current.add(id);

		logg('Energy intercepted by Rapier Sensor:', id);

		if (type === 'factory') {
			updatePaymentTallyFactory(energyColor);
		} else if (type === 'battery') {
			updatePaymentTallyBattery(energyColor);
		} else if (type === 'carbon') {
			updatePaymentTallyCarbon();
		}
	};

	function payForAction(action, cost) {
		resetPaymentParams();
		setSelectedAction(action);
		setSelectedActionCost(cost);

		setPaymentMode(true);
		// this will toggle on the payment box
		//

		// TODO payment buttons (AND HOW MUCH IS PAYED/OWED) using button-right, like <NavCluster />
	}

	function clientBuyPR() {
		payForAction(buyPR, 2);
	}

	function clientDonateEnergy() {
		payForAction(donateEnergy, 1);
	}

	function tutoralIncrement(){
		resetPaymentParams();
		setStepTutorial((prev) => prev + 1);
		clearTimeout(currentTickerTimer);
		setCurrentTickerIndex((prev) => prev + 1);
	}

	function clientStoreEnergy(){
		if(isTutorial && stepTutorial === 19){
			const currEnergy = myState.factoryEnergyArr.pop();
			myState.batteryEnergyArr.push(currEnergy);
			tutoralIncrement();
			return;
		}
		performActionTyped(storeEnergy);
	}

	function endMyTurn() {
		let chanceOfFunny = 100;

		if (Math.floor(Math.random() * chanceOfFunny) + 1 === 1) {
			logg('I END MY TURN');
			const audioSrc = my_turn_over_sfx;
			const audio = new Audio(audioSrc);
			audio.loop = false;
			audio.volume = 0.5; // volume;
			audio.play();
		}

		performActionTyped(endTurn);
	}

	if (!pc || pc == null) return null;
	if (!pc?.playerStateArray) return null;
	if (pc.playerStateArray.length < 1) return null;

	const isMyMat = pc.activePlayerId === pc.currPlayer.playerId && pc.activePlayerId === playerId;
	const shouldRender = tutorialCheck(tutStep_28) || (tutorialCheck(tutStep_10) && isMyMat);
	if (!shouldRender) return null;

	return (
		<>
			<group rotation={[0, rotato, 0]}>
				<PhysicsFloor>
					<mesh
						visible
						userData={{ hello: 'world' }}
						position={[0, 5, 15]}
						rotation={[0, 0, 0]}
						onClick={viewPlayermatModal}
					>
						<boxGeometry
							args={[playerMatWidth, 1, playerMatHeight]}
						/>
						<meshBasicMaterial
							attach="material"
							map={texture}
							side={DoubleSide}
						/>
					</mesh>
				</PhysicsFloor>

				{playerState && (
					<>
						<ActiveContractsAndAwardsBuilder
							playerId={playerId}
							activeContractsAndAwards={
								playerState.activeContractsAndAwards
							}
						/>

						<EquipmentBuilder
							playerId={playerId}
							equipment={playerState.equipment}
						/>

						<HandBuilder
							playerId={playerId}
							seasonalCards={playerState.seasonalCards}
							summitCards={playerState.summitCards}
							textureSS={textureSS}
							textureFW={textureFW}
							textureSummit={textureSummit}
						/>

						<CompletedContractsAndAwardsBuilder
							playerId={playerId}
							completedContractsAndAwards={
								playerState.completedContractsAndAwards
							}
						/>

						<FailedContractsBuilder
							playerId={playerId}
							failedContracts={playerState.failedContracts}
						/>

						{tutorialCheck(tutStep_12) &&
							<FactoryBuilder
								factories={playerState?.factories || 0}
								playerId={playerId}
								payForAction={payForAction}
							/>
						}
						{tutorialCheck(tutStep_15) &&
							<BatteryBuilder
								batteries={playerState?.batteries || 0}
								playerId={playerId}
								payForAction={payForAction}
							/>
						}

						<PrBuilder
							prArr={playerState.prArr}
							playerId={playerId}
						/>
						{/*
						<CarbonReserveBuilder
							carbonReserve={playerState.carbonReserve}
							playerId={playerId}
						/>
						*/}
						<CarbonFootprintBuilder
							carbonFootprint={playerState.carbonFootprint}
							playerId={playerId}
						/>
					</>
				)}

				{paymentMode && (
					<>
						<PaymentAreaBounded
							paymentAreaRef={paymentBoxRef}
							playerId={playerId}
							matColor={matColor}
							matPathId={matPathId}
							onEnergyEnter={handleEnergyPaymentZoneEntry}
						/>
					</>
				)}

				{pPE + pPC >= 2 && tutorialCheck(tutStep_30) && (
					<Button3D
						buttonAction={() => {
							clientBuyPR();
						}}
						playerId={playerId}
						p={[1, 5.5, 15.5]}
					/>
				)}

				{pPE > 0 &&
					myState.batteries > myState.batteryEnergyArr.length && tutorialCheck(tutStep_18) && (
						<Button3D
							buttonAction={() => {
								clientStoreEnergy();
							}}
							type={'store'}
							playerId={playerId}
							p={[8.5, 5, 13.5]}
						/>
					)}

				{tutorialCheck(tutStep_30) && !myState.modifiers.includes(carbonStolenThisTurn) && (
					<Button3D
						buttonAction={() => {
							performActionTyped(stealCarbon);
						}}
						type={'steal'}
						playerId={playerId}
						p={[6, 5.5, 15.5]}
					/>
				)}

				{pPE > 0 && tutorialCheck(tutStep_30) && (
					<Button3D
						buttonAction={() => {
							clientDonateEnergy();
						}}
						type={'donate'}
						playerId={playerId}
						p={[12, 5, 18.75]}
					/>
				)}

				{tutorialCheck(tutStep_30) &&
					<ButtonSquare3D
						buttonAction={() => {
							endMyTurn();
						}}
						playerId={playerId}
						p={[8.5, 5, 18]}
					/>
				}

				{tutorialCheck(tutStep_30) && showDonationCup && (
					<DonationCup
						playerId={playerId}
						matColor={matColor}
						matPathId={matPathId}
					/>
				)}
			</group>
			{playerState && (
				<>
					<EnergyBuilder
						energyArr={playerState.factoryEnergyArr}
						playerId={playerId}
						rotato={rotato}
					/>

					<EnergyBuilder
						energyArr={playerState.batteryEnergyArr}
						playerId={playerId}
						rotato={rotato}
						initPos={[-2, -0.5 + 7, 14.75]}
						type="battery"
					/>

					<EnergyBuilderCarbon
						numCarbon={playerState.carbonReserve}
						playerId={playerId}
						rotato={rotato}
						type="carbon"
					/>

					<EnergyBuilderDonation
						energyArr={playerState.donationCupArr}
						playerId={playerId}
						rotato={rotato}
					/>

					{/* TODO new energy builders to deal with donation cups */}
				</>
			)}
		</>
	);
};

export default PlayermatBuilder;
